import { normalizeUserRole } from './users.js'

const RESOURCE_TYPES = ['house', 'bathhouse', 'grill_house', 'banquet_hall']
const PRICE_UNITS = ['per_night', 'per_hour', 'per_group', 'per_booking']
const BOOKING_STATUSES = ['pending', 'confirmed', 'checked_in', 'completed', 'cancelled']
const INQUIRY_STATUSES = ['new', 'contacted', 'converted', 'closed']

export const resourceTypeLabels = {
  house: 'Дом',
  bathhouse: 'Баня',
  grill_house: 'Гриль-домик',
  banquet_hall: 'Банкетный зал',
}

export const resourceTypePluralLabels = {
  house: 'Дома',
  bathhouse: 'Бани',
  grill_house: 'Гриль-домики',
  banquet_hall: 'Банкетные залы',
}

export const priceUnitLabels = {
  per_night: 'за ночь',
  per_hour: 'за час',
  per_group: 'за компанию',
  per_booking: 'за бронирование',
}

export const bookingStatusLabels = {
  pending: 'Ожидает подтверждения',
  confirmed: 'Подтверждена',
  checked_in: 'Гости заехали',
  completed: 'Завершена',
  cancelled: 'Отменена',
}

export const inquiryStatusLabels = {
  new: 'Новая',
  contacted: 'В работе',
  converted: 'Бронь создана',
  closed: 'Закрыта',
}

const first = (source, keys, fallback = null) => {
  for (const key of keys) {
    if (source?.[key] !== undefined && source?.[key] !== null) return source[key]
  }
  return fallback
}

export function normalizeUser(raw = {}) {
  const username = first(raw, ['username', 'login', 'name'], 'Пользователь')
  const fullName = first(raw, ['full_name', 'display_name', 'name'], '')
  return {
    id: first(raw, ['id', 'user_id']),
    username,
    fullName,
    displayName: first(raw, ['display_name', 'full_name', 'name'], '') || username,
    role: normalizeUserRole(raw?.role),
    isActive: Boolean(first(raw, ['is_active', 'active'], true)),
  }
}

export function normalizeResource(raw = {}) {
  const type = first(raw, ['resource_type', 'type', 'kind'], 'house')
  return {
    id: first(raw, ['id', 'resource_id']),
    name: first(raw, ['name', 'title'], 'Без названия'),
    resourceType: RESOURCE_TYPES.includes(type) ? type : 'house',
    capacity: Number(first(raw, ['capacity', 'max_guests', 'places'], 0)) || 0,
    description: first(raw, ['description', 'notes'], ''),
    weekdayPrice: first(raw, ['weekday_price', 'weekdayPrice'], null),
    weekendPrice: first(raw, ['weekend_price', 'weekendPrice'], null),
    priceUnit: first(raw, ['price_unit', 'priceUnit'], 'per_booking'),
    isActive: Boolean(first(raw, ['is_active', 'active'], true)),
    color: first(raw, ['color'], null),
    createdAt: first(raw, ['created_at', 'createdAt']),
    updatedAt: first(raw, ['updated_at', 'updatedAt']),
    raw,
  }
}

export function serializeResource(resource) {
  return {
    name: resource.name.trim(),
    resource_type: resource.resourceType,
    capacity: Number(resource.capacity),
    description: resource.description?.trim() || '',
    weekday_price: resource.weekdayPrice === '' || resource.weekdayPrice == null
      ? null
      : Number(resource.weekdayPrice),
    weekend_price: resource.weekendPrice === '' || resource.weekendPrice == null
      ? null
      : Number(resource.weekendPrice),
    price_unit: PRICE_UNITS.includes(resource.priceUnit) ? resource.priceUnit : 'per_booking',
    is_active: resource.isActive,
    ...(resource.color ? { color: resource.color } : {}),
  }
}

export function normalizeBooking(raw = {}) {
  const nestedResource = first(raw, ['resource'], {}) || {}
  const status = first(raw, ['status', 'booking_status'], 'pending')
  return {
    id: first(raw, ['id', 'booking_id']),
    resourceId: first(raw, ['resource_id'], first(nestedResource, ['id', 'resource_id'])),
    resource: normalizeResource(nestedResource),
    guestName: first(raw, ['guest_name', 'customer_name', 'name'], 'Гость'),
    guestPhone: first(raw, ['guest_phone', 'phone'], ''),
    guestEmail: first(raw, ['guest_email', 'email'], ''),
    guestsCount: Number(first(raw, ['guests_count', 'guest_count', 'guests'], 1)) || 1,
    startsAt: first(raw, ['starts_at', 'start_at', 'check_in', 'start_date']),
    endsAt: first(raw, ['ends_at', 'end_at', 'check_out', 'end_date']),
    status: BOOKING_STATUSES.includes(status) ? status : 'pending',
    notes: first(raw, ['notes', 'comment'], ''),
    totalAmount: first(raw, ['total_amount', 'amount', 'price']),
    prepaidAmount: first(raw, ['prepaid_amount', 'prepayment', 'deposit']),
    createdAt: first(raw, ['created_at', 'createdAt']),
    updatedAt: first(raw, ['updated_at', 'updatedAt']),
    raw,
  }
}

export function serializeBooking(booking) {
  const payload = {
    resource_id: Number(booking.resourceId),
    guest_name: booking.guestName.trim(),
    guest_phone: booking.guestPhone?.trim() || '',
    guest_email: booking.guestEmail?.trim() || '',
    guests_count: Number(booking.guestsCount),
    starts_at: booking.startsAt,
    ends_at: booking.endsAt,
    status: booking.status,
    notes: booking.notes?.trim() || '',
  }

  if (booking.totalAmount !== '' && booking.totalAmount != null) {
    payload.total_amount = Number(booking.totalAmount)
  }
  if (booking.prepaidAmount !== '' && booking.prepaidAmount != null) {
    payload.prepaid_amount = Number(booking.prepaidAmount)
  }
  if (booking.changeReason?.trim()) {
    payload.change_reason = booking.changeReason.trim()
  }
  return payload
}

export function normalizeInquiry(raw = {}) {
  const nestedResource = first(raw, ['resource'], {}) || {}
  const status = first(raw, ['status'], 'new')
  return {
    id: first(raw, ['id']),
    externalId: first(raw, ['external_id'], ''),
    resourceId: first(raw, ['resource_id'], first(nestedResource, ['id'])),
    resource: normalizeResource(nestedResource),
    guestName: first(raw, ['guest_name'], 'Гость'),
    guestPhone: first(raw, ['guest_phone'], ''),
    guestEmail: first(raw, ['guest_email'], ''),
    guestsCount: first(raw, ['guests_count'], null),
    startsAt: first(raw, ['preferred_starts_at'], null),
    endsAt: first(raw, ['preferred_ends_at'], null),
    comment: first(raw, ['comment'], ''),
    status: INQUIRY_STATUSES.includes(status) ? status : 'new',
    bookingId: first(raw, ['booking_id'], null),
    createdAt: first(raw, ['created_at'], null),
    updatedAt: first(raw, ['updated_at'], null),
    raw,
  }
}

export function normalizeDashboard(raw = {}) {
  const summary = raw.summary || raw.metrics || {}
  const resourceLoad = first(raw, ['resource_load', 'resources'], []) || []
  const upcoming = first(raw, ['upcoming', 'upcoming_bookings', 'arrivals'], []) || []

  return {
    date: first(raw, ['date']),
    summary: {
      totalResources: Number(first(summary, ['total_resources', 'resources_total'], 0)) || 0,
      occupiedNow: Number(first(summary, ['occupied_now', 'occupied'], 0)) || 0,
      arrivalsToday: Number(first(summary, ['arrivals_today', 'arrivals'], 0)) || 0,
      departuresToday: Number(first(summary, ['departures_today', 'departures'], 0)) || 0,
      occupancyPercent: Number(first(summary, ['occupancy_percent', 'occupancy'], 0)) || 0,
    },
    resourceLoad: resourceLoad.map((entry) => {
      const hasDayBookings = Array.isArray(entry.bookings)
      const sourceBookings = hasDayBookings ? entry.bookings : []
      const currentBooking = entry.booking || entry.current_booking || null
      const dayBooking = hasDayBookings ? (sourceBookings[0] || null) : currentBooking
      return {
        resource: normalizeResource(entry.resource || entry),
        booking: dayBooking ? normalizeBooking(dayBooking) : null,
        currentBooking: currentBooking ? normalizeBooking(currentBooking) : null,
        dayBooking: dayBooking ? normalizeBooking(dayBooking) : null,
        currentStatus: entry.occupied_now === true
          ? 'occupied'
          : first(entry, ['current_status', 'status'], dayBooking ? 'occupied' : 'free'),
        occupiedNow: Boolean(first(entry, ['occupied_now'], currentBooking)),
        occupancyPercent: Number(first(entry, ['occupancy_percent'], 0)) || 0,
        bookings: sourceBookings.map(normalizeBooking),
        externalBlocks: Array.isArray(entry.external_blocks) ? entry.external_blocks : [],
        nextBooking: entry.next_booking ? normalizeBooking(entry.next_booking) : null,
      }
    }),
    upcoming: upcoming.map(normalizeBooking),
  }
}

export const options = {
  resourceTypes: RESOURCE_TYPES.map((value) => ({ value, label: resourceTypeLabels[value] })),
  bookingStatuses: BOOKING_STATUSES.map((value) => ({ value, label: bookingStatusLabels[value] })),
  inquiryStatuses: INQUIRY_STATUSES.map((value) => ({ value, label: inquiryStatusLabels[value] })),
}
